import React, { useMemo } from 'react';
import { Userprofile } from '../../Helpers/UsersContext';
import {
    HistoryEntry,
    FieldChange,
    ChangeAction,
    formatSwedishDate,
    translateFieldName,
    classifyChange,
    ACTION_LABELS,
} from '../../hooks/archive/useArchiveHistory';

// ── User Lookup Helper ───────────────────────────────────────────────

/**
 * Create a Map for O(1) user lookup from users array.
 * Use with useMemo in parent component for optimal performance.
 */
export function createUserMap(users: Userprofile[] | undefined): Map<string, string> {
    const map = new Map<string, string>();
    users?.forEach(u => map.set(u.key, u.name));
    return map;
}

export function getUserNameFromMap(uid: string | null, userMap: Map<string, string>): string {
    if (!uid) return 'Okänd användare';
    return userMap.get(uid) || 'Okänd användare';
}

// ── HistoryGroupBlock ────────────────────────────────────────────────

export interface HistoryGroupBlockProps {
    group: {
        groupKey: string;
        dateKey: string;
        userUids: string[];
        entries: HistoryEntry[];
    };
    userMap: Map<string, string>;
    loadingUsers: boolean;
    showEntityName?: boolean;
}

export const HistoryGroupBlock = React.memo<HistoryGroupBlockProps>(({
    group,
    userMap,
    loadingUsers,
    showEntityName = true,
}) => {
    const userLabel = useMemo(() => {
        if (loadingUsers) return '...';
        if (group.userUids.length === 0) return 'Okänd användare';
        if (group.userUids.length === 1) {
            return getUserNameFromMap(group.userUids[0], userMap);
        }
        return `${group.userUids.length} användare`;
    }, [loadingUsers, group.userUids, userMap]);

    return (
        <div className="level-history-day">
            <div className="level-history-day-header">
                <span className="level-history-date">{formatSwedishDate(group.dateKey)}</span>
                <span className="level-history-separator">&mdash;</span>
                <span className="level-history-user">{userLabel}</span>
            </div>
            <div className="level-history-entries">
                {group.entries.map((entry, i) => (
                    <HistoryEntryLine
                        key={`${entry.entityKey}-${entry.archiveKey}-${i}`}
                        entry={entry}
                        showEntityName={showEntityName}
                    />
                ))}
            </div>
        </div>
    );
});

HistoryGroupBlock.displayName = 'HistoryGroupBlock';

// ── HistoryEntryLine ─────────────────────────────────────────────────

export interface HistoryEntryLineProps {
    entry: HistoryEntry;
    showEntityName?: boolean;
}

export const HistoryEntryLine = React.memo<HistoryEntryLineProps>(({
    entry,
    showEntityName = true,
}) => {
    const { groups, actionOrder } = useMemo(() => {
        const g: Record<ChangeAction, FieldChange[]> = { added: [], changed: [], removed: [] };
        for (const change of entry.changes) {
            g[classifyChange(change)].push(change);
        }
        return {
            groups: g,
            actionOrder: ['changed', 'added', 'removed'] as ChangeAction[],
        };
    }, [entry.changes]);

    return (
        <div className="level-history-entry">
            {showEntityName && (
                <span className="level-history-entry-name">{entry.entityName}</span>
            )}
            <span className="level-history-entry-changes">
                {actionOrder.map((action) => {
                    const changes = groups[action];
                    if (changes.length === 0) return null;
                    return (
                        <span key={action} className="level-history-action-group">
                            <span className={`level-history-action level-history-action--${action}`}>
                                {ACTION_LABELS[action]}
                            </span>{' '}
                            {changes.map((change, i) => (
                                <React.Fragment key={change.field}>
                                    {i > 0 && ', '}
                                    {translateFieldName(change.field).toLowerCase()}
                                </React.Fragment>
                            ))}
                            {'. '}
                        </span>
                    );
                })}
            </span>
        </div>
    );
});

HistoryEntryLine.displayName = 'HistoryEntryLine';
